import { useMemo } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';
import type { TrackingProperties } from '@change-corgi/tracking-properties';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import type { FormValues } from '#src/app/pages/petitionDetails/shared/types';
import type { ConstituentIdentification } from '#src/app/shared/constituents';

import { useSignatureIntentTrackingData } from './intentData';
import { isSampled } from './shared/sampled';

export type SignatureIntentTrackingResult = Readonly<{
	trackSignIntent: (formData: FormValues, constituentIdentification?: ConstituentIdentification | null) => void;
	trackSignIntentContinue: (formData: FormValues) => void;
	trackSignIntentValid: (formData: FormValues) => void;
	trackPetitionActionsFlow: (petitionActionsEnabled: boolean) => void;
}>;

/**
 * The signer's constituent identification for `sign_intent` (CHANGE-100774), so a recruit's constituent
 * status can be joined to their `successful_recruit` in Redash. Empty when identification did not run.
 */
function toConstituentTrackingProperties(identification?: ConstituentIdentification | null): TrackingProperties {
	if (!identification) return {};
	return { is_constituent: identification.isConstituent, identification_method: identification.method };
}

export function useSignatureIntentTracking(): SignatureIntentTrackingResult {
	const track = useTracking();
	const {
		data: {
			featureConfigs: { signValidationIntentValidTrackSampleRate },
		},
	} = usePetitionDetailsPageData();
	const getTrackingData = useSignatureIntentTrackingData();

	return useMemo(
		() => ({
			trackSignIntent: (formData: FormValues, constituentIdentification?: ConstituentIdentification | null) => {
				void track('sign_intent', {
					...getTrackingData(formData),
					...toConstituentTrackingProperties(constituentIdentification),
				});
			},
			trackSignIntentContinue: (formData: FormValues) => {
				void track('sign_intent_continue', getTrackingData(formData));
			},
			trackSignIntentValid: (formData: FormValues) => {
				if (!isSampled(signValidationIntentValidTrackSampleRate)) return;

				void track('sign_intent_valid', getTrackingData(formData));
			},
			trackPetitionActionsFlow: (petitionActionsEnabled: boolean) => {
				void track('petition_actions_flow', { enabled: petitionActionsEnabled });
			},
		}),
		[track, signValidationIntentValidTrackSampleRate, getTrackingData],
	);
}
